import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, Play, Pause, CheckCircle2, ExternalLink, Copy, Facebook, Instagram, Linkedin, Globe, X } from 'lucide-react';

const FACEBOOK_SLIDES = [
  {
    title: "Step 1: Open Meta Business Suite",
    body: "Go to business.facebook.com and log in with the Facebook account that manages your business page.",
    highlight: "If you don't have a Facebook Business Page yet, create one first at facebook.com/pages/create",
    action: { label: "Open Meta Business Suite", url: "https://business.facebook.com" },
    tip: "Use a computer (not phone) for the easiest setup experience.",
  },
  {
    title: "Step 2: Select Your Business Page",
    body: "From the left sidebar, click your Business Page name. If you manage multiple pages, make sure you select the correct one for this business.",
    highlight: "Don't see your page? You may need to claim it or request admin access from the current page owner.",
    tip: "Only Page Admins can set up auto-posting. Check your role under Page Settings > Page Roles.",
  },
  {
    title: "Step 3: Create & Schedule Posts",
    body: "Click 'Create Post' in Meta Business Suite. Write your post content, add photos/videos, then click the dropdown arrow next to 'Publish' and select 'Schedule Post'.",
    highlight: "Meta Business Suite lets you schedule posts for both Facebook AND Instagram from one place!",
    tip: "Best posting times for local businesses: Tue-Thu, 9-11 AM and 1-3 PM local time.",
  },
  {
    title: "Step 4: Connect Instagram (Optional)",
    body: "In Meta Business Suite, go to Settings > Accounts > Instagram accounts. Click 'Connect Account' and log in with the business Instagram credentials.",
    highlight: "Your Instagram must be a Business or Creator account (not Personal) to connect it.",
    tip: "To switch: Instagram App > Settings > Account > Switch to Professional Account.",
  },
  {
    title: "Step 5: Set Up Auto-Posting",
    body: "For fully automated posting, we'll use your connected page to schedule content. Go to the Planner tab in Meta Business Suite to see your content calendar and manage all scheduled posts.",
    highlight: "Once connected, our platform can generate AI content suggestions that you copy-paste into Meta Business Suite's scheduler.",
    tip: "You can also use the Meta Business Suite mobile app to manage posts on the go!",
  },
  {
    title: "You're All Set!",
    body: "Your Facebook and Instagram are now configured for business posting. Here's what you can do:",
    highlight: "Schedule posts directly in Meta Business Suite\nUse our AI-generated content suggestions\nTrack engagement from your Social dashboard\nManage both FB + IG from one place",
    tip: "Need help? Contact your account manager and we'll walk you through it live.",
    isSummary: true,
  },
];

const INSTAGRAM_SLIDES = [
  {
    title: "Step 1: Switch to Business Account",
    body: "Open the Instagram app. Go to Settings > Account > Switch to Professional Account. Choose 'Business' (not Creator).",
    highlight: "A Business account unlocks: contact button, insights, ad tools, and integration with Meta Business Suite.",
    tip: "You won't lose any followers or posts when switching.",
  },
  {
    title: "Step 2: Connect to Your Facebook Page",
    body: "During the switch, Instagram will ask you to connect a Facebook Page. Select your business's Facebook Page. This links them for cross-posting.",
    highlight: "If you don't have a Facebook Page yet, you can create one during this step or skip and connect later.",
    tip: "Having both connected lets you schedule posts for FB + IG simultaneously!",
  },
  {
    title: "Step 3: Complete Your Profile",
    body: "Fill in your business details: category, contact info (phone, email, address), bio, profile photo, and website link.",
    highlight: "A complete Instagram profile gets 30% more engagement. Use your logo as the profile pic.",
    tip: "Use a link-in-bio tool (like Linktree) to share multiple links from your profile.",
  },
  {
    title: "Step 4: Schedule via Meta Business Suite",
    body: "Once connected, go to business.facebook.com > Planner. You can create and schedule Instagram posts, Stories, and Reels from your computer.",
    highlight: "This is the free, official way to schedule Instagram posts without any third-party tools!",
    tip: "Instagram Reels get 2-3x more reach than regular posts in 2026.",
  },
];

const LINKEDIN_SLIDES = [
  {
    title: "Step 1: Create a LinkedIn Company Page",
    body: "Go to linkedin.com/company/setup/new and fill in your business details. You need a personal LinkedIn account to create a company page.",
    highlight: "Choose 'Company' (not Showcase). Use your exact business name as it appears on Google.",
    action: { label: "Create LinkedIn Page", url: "https://www.linkedin.com/company/setup/new/" },
    tip: "You must have a personal LinkedIn profile to be the Page admin.",
  },
  {
    title: "Step 2: Complete Your Company Profile",
    body: "Add your logo, cover image, description (use keywords!), website URL, industry, company size, and location.",
    highlight: "Complete profiles get 30% more weekly views. Write 3-4 sentences for the About section.",
    tip: "Use a 300x300px logo and 1128x191px cover image for best quality.",
  },
  {
    title: "Step 3: Invite Connections",
    body: "Click 'Admin Tools' > 'Invite Connections' to invite your personal network to follow your business page. This builds your initial audience.",
    highlight: "LinkedIn gives you 250 invite credits per month. Use them!",
    tip: "Ask employees and partners to list your company as their employer for more visibility.",
  },
  {
    title: "Step 4: Start Posting",
    body: "Click 'Start a post' on your Company Page. Share business updates, industry insights, team highlights, and customer success stories.",
    highlight: "LinkedIn posts with images get 2x more engagement. Post 2-3 times per week.",
    tip: "Best times to post: Tue-Thu, 8-10 AM and 12-1 PM. B2B audiences are most active mid-week.",
  },
  {
    title: "Step 5: Connect to LocalBrandingPro",
    body: "Once your LinkedIn Company Page is set up, let your account manager know. We'll connect it for automated AI-powered posting directly from your dashboard.",
    highlight: "After connection, our system will auto-generate and schedule professional LinkedIn posts based on your business topics.",
    tip: "LinkedIn is especially powerful for B2B businesses, professional services, and agencies.",
    isSummary: true,
  },
];

const GBP_SLIDES = [
  {
    title: "Connect Your Google Business Profile",
    body: "Connecting your Google Business Profile lets us manage your listing directly from this dashboard. Here's how:",
    highlight: "1. Go to the GBP Audit tab in your sidebar\n2. Click 'Connect with Google'\n3. Sign in with the Google account that manages your business listing\n4. Allow access — that's it!",
    isSummary: false,
    tip: "Not sure which Google account? It's the one you use to sign into business.google.com",
  },
  {
    title: "What You Get After Connecting",
    body: "Once connected, your dashboard unlocks powerful GBP management tools:",
    highlight: "Respond to Google Reviews directly from your dashboard\nSchedule and publish Google Business posts\nRun AI-powered profile audits with optimization tips\nTrack your GBP score over time\nAutomatic posting to your listing",
    isSummary: true,
    tip: "Visit the GBP Audit tab in your sidebar to connect and get started!",
  },
];

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook & Instagram', Icon: Facebook, color: '#1877f2', slides: FACEBOOK_SLIDES, description: 'Schedule posts via Meta Business Suite' },
  { id: 'instagram', label: 'Instagram Setup', Icon: Instagram, color: '#e4405f', slides: INSTAGRAM_SLIDES, description: 'Switch to Business account & connect' },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, color: '#0a66c2', slides: LINKEDIN_SLIDES, description: 'Create & optimize your Company Page' },
  { id: 'gbp', label: 'Google Business', Icon: Globe, color: '#4285f4', slides: GBP_SLIDES, description: 'Managed from your dashboard' },
];

function Slideshow({ slides, platformColor, onClose }) {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const slide = slides[current];

  const next = useCallback(() => {
    setCurrent(i => (i < slides.length - 1 ? i + 1 : i));
  }, [slides.length]);

  const prev = () => setCurrent(i => (i > 0 ? i - 1 : i));

  useEffect(() => {
    if (playing && current < slides.length - 1) {
      timerRef.current = setTimeout(next, 8000);
    }
    return () => clearTimeout(timerRef.current);
  }, [playing, current, next, slides.length]);

  const togglePlay = () => {
    setPlaying(p => !p);
    if (!playing) clearTimeout(timerRef.current);
  };

  const progress = ((current + 1) / slides.length) * 100;

  return (
    <div className="space-y-4" data-testid="slideshow-container">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: platformColor }} />
        </div>
        <span className="text-xs text-gray-500 font-medium tabular-nums">{current + 1} / {slides.length}</span>
        <Button size="sm" variant="ghost" onClick={onClose} className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600" data-testid="close-slideshow">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Slide content */}
      <Card className="bg-white border-gray-200 overflow-hidden">
        <div className="h-1" style={{ backgroundColor: platformColor }} />
        <CardContent className="p-6 min-h-[280px] flex flex-col">
          <h3 className="text-base font-bold text-gray-900 mb-3" data-testid="slide-title">{slide.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">{slide.body}</p>

          {slide.highlight && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              {slide.highlight.split('\n').map((line, i) => (
                <p key={i} className="text-xs text-blue-800 flex items-start gap-2 mb-1 last:mb-0">
                  {slide.isSummary && <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />}
                  <span>{line}</span>
                </p>
              ))}
            </div>
          )}

          {slide.action && (
            <a href={slide.action.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors mb-4 w-fit"
              data-testid="slide-action-link">
              <ExternalLink className="w-3 h-3" /> {slide.action.label}
            </a>
          )}

          <div className="mt-auto pt-3 border-t border-gray-100">
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded px-2.5 py-1.5 inline-block">
              <strong>Tip:</strong> {slide.tip}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={prev} disabled={current === 0}
          className="text-xs text-gray-500 disabled:opacity-30" data-testid="slide-prev">
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>

        <Button size="sm" variant="ghost" onClick={togglePlay}
          className="h-8 w-8 p-0 rounded-full border border-gray-200" data-testid="slide-playpause">
          {playing ? <Pause className="w-3.5 h-3.5 text-gray-600" /> : <Play className="w-3.5 h-3.5 text-gray-600 ml-0.5" />}
        </Button>

        <Button size="sm" variant="ghost" onClick={next} disabled={current === slides.length - 1}
          className="text-xs text-gray-500 disabled:opacity-30" data-testid="slide-next">
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export default function SocialSetupGuide({ client, getHeaders }) {
  const [activePlatform, setActivePlatform] = useState(null);
  const [gbpConnected, setGbpConnected] = useState(false);
  const [metaStatus, setMetaStatus] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/client/dashboard/gbp/status`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setGbpConnected(d?.connected || false))
      .catch(() => {});
    fetch(`${API}/api/client/dashboard/meta/status`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setMetaStatus(d))
      .catch(() => {});
  }, [getHeaders]);

  const connectMeta = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${API}/api/auth/meta/authorize?client_slug=${client?.slug}`;
  };

  if (activePlatform) {
    const platform = PLATFORMS.find(p => p.id === activePlatform);
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <platform.Icon className="w-5 h-5" style={{ color: platform.color }} />
          <h3 className="text-sm font-semibold text-gray-900">{platform.label} Setup Guide</h3>
        </div>
        <Slideshow
          slides={platform.slides}
          platformColor={platform.color}
          onClose={() => setActivePlatform(null)}
        />
      </div>
    );
  }

  const metaConnected = metaStatus?.connected;

  return (
    <div className="space-y-4" data-testid="social-setup-guide">
      {/* Connect Facebook OAuth Banner */}
      <Card className={`border ${metaConnected ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
        <CardContent className="p-4 flex items-center justify-between">
          {metaConnected ? (
            <>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Facebook & Instagram Connected</p>
                  <p className="text-xs text-green-600">
                    {metaStatus.pages?.length || 0} page(s) linked
                    {metaStatus.pages?.[0]?.page_name && ` — ${metaStatus.pages[0].page_name}`}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Facebook className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Connect Facebook & Instagram</p>
                  <p className="text-xs text-blue-600">One-click setup — authorize your Page for automated posting</p>
                </div>
              </div>
              <Button size="sm" onClick={connectMeta} className="bg-[#1877f2] hover:bg-[#166fe5] text-white text-xs" data-testid="connect-meta-btn">
                <Facebook className="w-3 h-3 mr-1" /> Connect with Facebook
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-gray-900">Platform Setup Guides</h3>
        <p className="text-xs text-gray-500 mt-0.5">Step-by-step tutorials for each platform</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PLATFORMS.map(p => {
          const isGbp = p.id === 'gbp';
          const isFb = p.id === 'facebook' || p.id === 'instagram';
          const connected = (isGbp && gbpConnected) || (isFb && metaConnected);
          return (
            <Card key={p.id}
              className={`border cursor-pointer transition-all hover:shadow-md ${connected ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              onClick={() => setActivePlatform(p.id)}
              data-testid={`setup-guide-${p.id}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${p.color}15` }}>
                  <p.Icon className="w-5 h-5" style={{ color: p.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{p.label}</p>
                    {connected && <Badge className="bg-green-100 text-green-700 text-[9px]">Connected</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
